<template>
  <b-modal
    v-model="localShowDetailModal"
    :title="
      'Detail Experiential Learning - ' + (selectedTNA?.projectName || '')
    "
    size="lg"
    hide-footer
    @hide="$emit('close')"
  >
    <div v-if="isLoadingModal" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading data...</p>
    </div>
    <div v-if="selectedTNA">
      <div class="row g-0">
        <!-- Full Width - Course Info -->
        <div class="col-md-12">
          <!-- Header Section -->
          <div class="row mb-4">
            <div class="col-md-7 d-flex flex-column justify-content-between">
              <div class="d-flex flex-column">
                <!-- Status Badge -->
                <div class="mb-3">
                  <b-badge
                    :variant="getStatusVariant(selectedTNA.status)"
                    class="completion-badge"
                  >
                    {{
                      selectedTNA.status
                        ? selectedTNA.status.charAt(0).toUpperCase() +
                          selectedTNA.status.slice(1).toLowerCase()
                        : "-"
                    }}
                  </b-badge>
                </div>

                <!-- Course Title -->
                <h3 class="mb-3 font-weight-bold" style="color: #1a202c">
                  {{ selectedTNA.projectName || "-" }}
                </h3>
              </div>

              <!-- Stats -->
              <div class="row p-3 rounded" style="background-color: #fafafa">
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i class="fe fe-clock me-2" style="font-size: 1.1rem"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem"
                        >Duration</small
                      >
                      <strong class="text-dark">{{
                        selectedTNA?.project?.duration
                          ? selectedTNA.project.duration + " days"
                          : "-"
                      }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i
                      class="fe fe-calendar me-2"
                      style="font-size: 1.1rem"
                    ></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem"
                        >Start Date</small
                      >
                      <strong class="text-dark">{{
                        selectedTNA?.project?.start_date || "-"
                      }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i
                      class="fe fe-calendar me-2"
                      style="font-size: 1.1rem"
                    ></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem"
                        >End Date</small
                      >
                      <strong class="text-dark">{{
                        selectedTNA?.project?.end_date || "-"
                      }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side - Course Image -->
            <div class="col-md-5">
              <img
                :src="
                  selectedTNA && selectedTNA.cover_image_path
                    ? `${selectedTNA.cover_image_path}`
                    : `https://dummyimage.com/300x200/FAFAFA/000000&text=${
                        selectedTNA.title || 'Course'
                      }`
                "
                alt="Course Thumbnail"
                class="w-100 rounded"
                style="height: 280px; object-fit: cover"
              />
            </div>

            <!-- Tabs -->
            <ul class="nav nav-tabs mb-3" role="tablist">
              <li class="nav-item">
                <a
                  class="nav-link active"
                  data-bs-toggle="tab"
                  href="#description"
                >
                  Description
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#attributes">
                  Attributes
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#project-teams">
                  Project Teams
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#milestone">
                  Milestone
                </a>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="tab-content">
              <!-- Description Tab -->
              <div class="tab-pane fade show active" id="description">
                <p class="mb-3" style="line-height: 1.8; font-size: 0.9rem">
                  {{ selectedTNA.description || "No description available." }}
                </p>
              </div>
              <!-- Attribute Tab -->
              <div class="tab-pane fade" id="attributes">
                <div class="row">
                  <!-- Left Column -->
                  <div class="col-md-6">
                    <div
                      class="mb-4 py-2 px-3 rounded"
                      style="background-color: #fafafa"
                    >
                      <label class="text-muted small mb-2">Business Need</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ selectedTNA?.project?.business_need || "-" }}
                      </p>
                    </div>

                    <div
                      class="mb-4 py-2 px-3 rounded"
                      style="background-color: #fafafa"
                    >
                      <label class="text-muted small mb-2">Objectives</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ selectedTNA?.project?.objectives || "-" }}
                      </p>
                    </div>

                    <div
                      class="mb-4 py-2 px-3 rounded"
                      style="background-color: #fafafa"
                    >
                      <label class="text-muted small mb-2">Goals</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ selectedTNA?.project?.goals || "-" }}
                      </p>
                    </div>

                    <div
                      class="mb-4 py-2 px-3 rounded"
                      style="background-color: #fafafa"
                    >
                      <label class="text-muted small mb-2">Scope</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ selectedTNA?.project?.scope || "-" }}
                      </p>
                    </div>

                    <div
                      class="mb-4 py-2 px-3 rounded"
                      style="background-color: #fafafa"
                    >
                      <label class="text-muted small mb-2">Constraints</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ selectedTNA?.project?.constraints || "-" }}
                      </p>
                    </div>
                  </div>

                  <!-- Right Column -->
                  <div class="col-md-6">
                    <div
                      class="mb-4 py-2 px-3 rounded"
                      style="background-color: #fafafa"
                    >
                      <label class="text-muted small mb-2">Deliverables</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ selectedTNA?.project?.deliverables || "-" }}
                      </p>
                    </div>

                    <div
                      class="mb-4 py-2 px-3 rounded"
                      style="background-color: #fafafa"
                    >
                      <label class="text-muted small mb-2">Impact A</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ selectedTNA?.project?.impact_a || "-" }}
                      </p>
                    </div>

                    <div
                      class="mb-4 py-2 px-3 rounded"
                      style="background-color: #fafafa"
                    >
                      <label class="text-muted small mb-2">Impact B</label>
                      <p class="text-dark font-weight-bold mb-0">
                        {{ selectedTNA?.project?.impact_b || "-" }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Project Teams Tab -->
              <div class="tab-pane fade" id="project-teams">
                <b-row>
                  <b-col md="12" class="mt-4">
                    <b-card-title>Project Teams</b-card-title>
                    <b-card-text v-if="selectedTNA?.teams?.length">
                      <table class="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th>Role</th>
                            <th>NIK</th>
                            <th>Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(item, index) in selectedTNA?.teams"
                            :key="index"
                          >
                            <td>{{ item.role }}</td>
                            <td>{{ item.nik }}</td>
                            <td>{{ item.name }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </b-card-text>
                    <b-card-text v-else>No data available</b-card-text>
                  </b-col>
                </b-row>
              </div>
              <!-- Milestone Tab -->
              <div class="tab-pane fade" id="milestone">
                <h4>Milestone</h4>
                <div class="mt-4 float-end mb-4">
                  <b-button
                    @click="$emit('open-add-activities')"
                    variant="primary"
                    ><span class="fe fe-plus"></span>Add Milestone</b-button
                  >
                </div>
                <table class="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Actual Start Date</th>
                      <th>Actual End Date</th>
                      <th>Progress (%)</th>
                      <th>Status</th>
                      <th>Evidence File</th>
                      <th>Feedback</th>
                      <th>Assigned</th>
                    </tr>
                  </thead>
                  <tbody
                    v-if="
                      selectedTNA &&
                      selectedTNA.milestones &&
                      selectedTNA.milestones.length
                    "
                  >
                    <tr
                      v-for="(item, index) in selectedTNA.milestones"
                      :key="index"
                    >
                      <td>{{ item.task }}</td>
                      <td>{{ formatDate(item.start_date) }}</td>
                      <td>{{ formatDate(item.end_date) }}</td>
                      <td>{{ formatDate(item.actual_start_date) || "-" }}</td>
                      <td>{{ formatDate(item.actual_end_date) || "-" }}</td>
                      <td>{{ item.new_progress }}</td>
                      <td>
                        <span
                          class="badge"
                          :class="statusBadgeClass(item.status)"
                        >
                          {{ capitalizeStatus(item.status) }}
                        </span>
                      </td>
                      <td>
                        <a
                          v-if="item.evidence && item.evidence.length > 0"
                          :href="item.evidence[0].url"
                          target="_blank"
                          >Files</a
                        >
                        <span v-else>-</span>
                      </td>
                      <td>
                        <ul v-if="item.feedback && item.feedback.length">
                          <li
                            v-for="(feedback, fIndex) in item.feedback"
                            :key="fIndex"
                          >
                            {{ feedback.feedback }}
                          </li>
                        </ul>
                        <span v-else>-</span>
                      </td>
                      <td>
                        <ul v-if="item.members && item.members.length">
                          <li
                            v-for="(employee, eIndex) in item.members"
                            :key="eIndex"
                          >
                            {{ employee?.name }}
                          </li>
                        </ul>
                        <span v-else>-</span>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td class="text-center" colspan="10">
                        No Data Available
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AddActivitiesExperiential
      v-model:show="isAddActivities"
      @submit="handleSubmitActivities"
    />
  </b-modal>
</template>

<script>
import AddActivitiesExperiential from "./AddActivitiesExperiential.vue";

export default {
  name: "DetailTNA",
  components: { AddActivitiesExperiential },
  data: {
    isAddActivities: false,
  },
  props: {
    showDetailModal: {
      type: Boolean,
      default: false,
    },
    isLoadingModal: {
      type: Boolean,
      default: false,
    },
    selectedTNA: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    localShowDetailModal: {
      get() {
        return this.showDetailModal;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      },
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB"); // DD/MM/YYYY
    },
    capitalizeStatus(value) {
      if (!value) return "";
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    },
    statusBadgeClass(status) {
      switch (status) {
        case "completed":
          return "bg-success text-white";
        case "in progress":
          return "bg-warning text-dark";
        case "to do":
          return "bg-secondary text-white";
        default:
          return "bg-light text-dark";
      }
    },
    getStatusVariant(status) {
      switch (status) {
        case "approved":
          return "success";
        case "rejected":
          return "danger";
        case "pending":
          return "warning";
        default:
          return "secondary";
      }
    },
    openAddActivities() {
      this.isAddActivities = true;
    },
    handleSubmitActivities(payload) {
      // TODO:
      // await api.createMilestone(payload)

      this.isAddActivities = false; // parent yang nutup
    },
  },
};
</script>

<style scoped>
.gap-2 > * + * {
  margin-left: 0.5rem;
}

.table td {
  padding: 0.5rem 0;
}

.d-flex.flex-wrap {
  row-gap: 0.25rem;
  column-gap: 0.25rem;
}

.completion-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}
</style>
